// frontend/src/services/authApi.js
import { API_ORIGIN } from './apiOrigin';
import { presenceDiag } from '../utils/presenceDiag';
import { tokenStore } from '../platform/tokenStore';

export const API_BASE = API_ORIGIN;

// === JWT helpers & refresh state ===
function decodeJwt(token) {
  try {
    const parts = String(token).split('.')
    if (parts.length < 2) return null;
    // base64url -> base64
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // pad to multiple of 4
    const padded = payload + '==='.slice((payload.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch { return null; }
}

export function isTokenValid(token) {
  if (!token) return false;
  const p = decodeJwt(token);
  if (!p || !p.exp) return false;
  // exp is in seconds; add small skew tolerance
  return (p.exp * 1000) > (Date.now() + 5000);
}

let refreshPromise = null;
let invalidationNotified = false;

async function refreshSession(context = {}) {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const requestUrl = `${API_BASE}/api/auth/refresh`;
    const pageOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    console.info({
      event: 'auth:refresh_attempt',
      requestUrl,
      pageOrigin,
      credentialsMode: 'include',
      accessTokenPresent: Boolean(getStoredToken()),
      timestamp: new Date().toISOString(),
    });

    presenceDiag('auth:refresh:start', { path: context.path || requestUrl, source: context.source });
    const res = await fetch(requestUrl, {
      method: 'POST',
      credentials: 'include',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      presenceDiag('auth:refresh:failure', { path: context.path || requestUrl, source: context.source, status: res.status, reason: data?.error || data?.reason || 'unknown' });
      console.warn({
        event: 'auth:refresh_failed',
        status: res.status,
        responseReason: data?.error || data?.reason || null,
        requestUrl,
        pageOrigin,
        timestamp: new Date().toISOString(),
      });
      const error = new Error('refresh_failed');
      error.status = res.status;
      error.reason = data?.error || data?.reason || null;
      throw error;
    }
    const newToken = data.accessToken || data.token;
    if (!newToken) throw new Error('no_access_token');
    presenceDiag('auth:refresh:success', { path: context.path || requestUrl, source: context.source, extra: { beforeTokenPresent: Boolean(getStoredToken()), afterTokenLength: String(newToken).length } });
    await setStoredToken(newToken, { refreshed: true });
    try {
      const { updateSocketAuthenticationToken } = await import('./socket');
      updateSocketAuthenticationToken();
    } catch {}
    invalidationNotified = false;
    return newToken;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

function requestUrl(path) {
  const raw = String(path || '');
  return /^https?:\/\//i.test(raw) ? raw : `${API_BASE}${raw}`;
}

function isBodyInit(value) {
  return typeof value === 'string' ||
    (typeof FormData !== 'undefined' && value instanceof FormData) ||
    (typeof Blob !== 'undefined' && value instanceof Blob) ||
    (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) ||
    (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer);
}

function buildHeaders(headers, authToken, shouldSerializeJson) {
  const merged = {};
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    headers.forEach((value, key) => {
      if (key.toLowerCase() !== 'authorization') merged[key] = value;
    });
  } else if (Array.isArray(headers)) {
    for (const [key, value] of headers) {
      if (String(key).toLowerCase() !== 'authorization') merged[key] = value;
    }
  } else {
    for (const [key, value] of Object.entries(headers || {})) {
      if (key.toLowerCase() !== 'authorization') merged[key] = value;
    }
  }
  if (shouldSerializeJson && !Object.keys(merged).some((key) => key.toLowerCase() === 'content-type')) {
    merged['Content-Type'] = 'application/json';
  }
  if (authToken) merged.Authorization = `Bearer ${authToken}`;
  return merged;
}

async function parseResponse(res) {
  if (res.status === 204) return { data: null };
  try {
    return { data: await res.json() };
  } catch {
    return { data: null };
  }
}

function responseError(res, data) {
  const msg = (data && (data.message || data.error)) || `HTTP ${res.status}`;
  const err = new Error(msg);
  err.status = res.status;
  err.payload = data;
  return err;
}

async function req(path, options = {}) {
  const {
    token,
    body,
    rawResponse = false,
    diagSource,
    headers,
    method = 'GET',
    credentials = 'include',
    ...fetchOptions
  } = options;
  const url = requestUrl(path);
  const isRefreshExcludedEndpoint = /\/api\/auth\/(login|register|logout|refresh|me)\b/.test(url);
  const shouldSerializeJson = body != null && !isBodyInit(body);
  const requestBody = body == null ? undefined : (shouldSerializeJson ? JSON.stringify(body) : body);

  const doFetch = async (authToken, retry) => {
    presenceDiag('auth:req:start', { path: url, source: diagSource, retry });
    const res = await fetch(url, {
      ...fetchOptions,
      method,
      credentials,
      headers: buildHeaders(headers, authToken, shouldSerializeJson),
      ...(requestBody !== undefined ? { body: requestBody } : {}),
    });
    presenceDiag('auth:req:response', { path: url, source: diagSource, status: res.status, retry });
    return res;
  };

  // Central storage is authoritative. `token` remains only as a compatibility
  // fallback for legacy callers that execute before a token has been stored.
  let activeToken = getStoredToken() || token || null;
  let refreshedBeforeRequest = false;
  if (!isRefreshExcludedEndpoint && activeToken && !isTokenValid(activeToken)) {
    presenceDiag('auth:req:token-expired', { path: url, source: diagSource });
    try {
      activeToken = await refreshSession({ path: url, source: diagSource });
      refreshedBeforeRequest = true;
    } catch (error) {
      if (isConfirmedInvalidSession(error)) notifyConfirmedInvalidSession(error, { path: url, source: diagSource });
      throw error;
    }
  }

  let res = await doFetch(activeToken, false);
  let parsed = rawResponse ? null : await parseResponse(res);
  let data = parsed?.data ?? null;

  // A protected endpoint's first 401 is sufficient evidence that the access
  // credential needs one refresh attempt. Backend payload wording is not part
  // of refresh eligibility. 419 remains supported for existing callers, while
  // a generic 403 is an authorization failure and must remain observable.
  const refreshableStatus = res.status === 401 || res.status === 419;
  if (!isRefreshExcludedEndpoint && !refreshedBeforeRequest && refreshableStatus) {
    if (rawResponse && res.body && typeof res.body.cancel === 'function') {
      try { await res.body.cancel(); } catch {}
    }
    try {
      presenceDiag('auth:req:refresh-branch', { path: url, source: diagSource, status: res.status });
      activeToken = await refreshSession({ path: url, source: diagSource });
    } catch (error) {
      if (isConfirmedInvalidSession(error)) notifyConfirmedInvalidSession(error, { path: url, source: diagSource });
      throw error;
    }
    presenceDiag('auth:req:retry-begin', { path: url, source: diagSource, retry: true });
    res = await doFetch(activeToken, true);
    parsed = rawResponse ? null : await parseResponse(res);
    data = parsed?.data ?? null;
  }

  if (rawResponse) return res;
  if (!res.ok) throw responseError(res, data);
  return data ?? {};
}

// Shared authenticated request path for feature services.  It owns token
// selection, the single in-flight refresh, and the one retry after refresh.
// Callers receive parsed JSON and never need to know that refresh exists.
export function authenticatedRequest(path, options) {
  return req(path, options);
}

// Helpers de token en localStorage (centralizados)
export function getStoredToken() {
  try { return tokenStore.getAccessToken(); } catch { return null; }
}
export async function setStoredToken(token, { refreshed = false } = {}) {
  if (token) {
    await tokenStore.setAccessToken(token);
    invalidationNotified = false;
    const browserEventToken = tokenStore.kind === 'web-local-storage' ? token : null;
    if (refreshed) {
      window.dispatchEvent(new CustomEvent('auth:tokenRefreshed', { detail: browserEventToken }));
    } else {
      // Notify other tabs/listeners
      window.dispatchEvent(new StorageEvent('storage', { key: 'token', newValue: browserEventToken }));
      window.dispatchEvent(new StorageEvent('storage', { key: 'accessToken', newValue: browserEventToken }));
      window.dispatchEvent(new CustomEvent('token:changed', { detail: browserEventToken }));
    }
    return true;
  }
  return false;
}
export async function clearStoredToken() {
  try {
    presenceDiag('auth:clear-stored-token', { reason: 'dispatch_z10:authInvalidated' });
    tokenStore.clearAccessTokenImmediate();
    window.dispatchEvent(new StorageEvent('storage', { key: 'token', newValue: null }));
    window.dispatchEvent(new StorageEvent('storage', { key: 'accessToken', newValue: null }));
    window.dispatchEvent(new CustomEvent('token:changed', { detail: null }));
    // Any feature that conclusively clears credentials must also enter the
    // single AuthContext cleanup path (state, socket, and sensitive caches).
    window.dispatchEvent(new CustomEvent('z10:authInvalidated'));
    await tokenStore.clearAccessToken();
  } catch {}
}

function isConfirmedInvalidSession(error) {
  const status = Number(error?.status || 0);
  const reason = String(error?.reason || '');
  const terminalReasons = new Set([
    'no_cookie', 'invalid_refresh', 'expired', 'no_matching_secret',
    'invalid_payload', 'invalid_signature',
  ]);
  return (status === 401 || status === 403 || status === 419) && terminalReasons.has(reason);
}

function notifyConfirmedInvalidSession(error, context = {}) {
  if (invalidationNotified) return;
  invalidationNotified = true;
  presenceDiag('auth:confirmed-invalid-session', { path: context.path, source: context.source, status: error?.status, reason: error?.reason || error?.message || 'unknown' });
  clearStoredToken();
}

export async function requestServerLogout({ token = null } = {}) {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
  } catch {
    // Local logout remains mandatory even if the request cannot reach the API.
  }
}

export async function requestAccountDeletion({ token = null } = {}) {
  const response = await authenticatedRequest(`${API_BASE}/api/auth/account`, {
    method: 'DELETE',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    rawResponse: true,
    diagSource: 'authApi.requestAccountDeletion',
  });
  if (!response.ok) {
    const error = new Error('account_deletion_failed');
    error.status = response.status;
    throw error;
  }
  return true;
}

export function broadcastAuthLogout() {
  try { localStorage.setItem('z10:auth_logout', String(Date.now())); } catch {}
}

/* ========================
   Endpoints de Auth
   ======================== */

// Solicitar email de recuperación
export async function requestPasswordReset(email) {
  return req('/api/auth/forgot-password', {
    method: 'POST',
    body: { email },
  });
}

// Cambiar contraseña logueado
export async function changePassword(authToken, { currentPassword, newPassword }) {
  return req('/api/auth/change-password', {
    method: 'POST',
    token: authToken,
    body: { currentPassword, newPassword },
  });
}

// Traer info de la sesión actual (valida el token en backend)
export async function getMe(authToken) {
  const token = authToken || getStoredToken();
  return req('/api/auth/me', {
    method: 'GET',
    token,
  });
}

// Resetear con token recibido por email
export async function resetPasswordWithToken(tokenFromEmail, newPassword) {
  return req('/api/auth/reset-password', {
    method: 'POST',
    body: { token: tokenFromEmail, password: newPassword },
  });
}

/* ========================
   Alias de compatibilidad
   (por si tus pantallas estaban importando otros nombres)
   ======================== */

export const forgotPassword = requestPasswordReset;     // alias
export const resetPassword = resetPasswordWithToken;    // alias

// Session helpers exposed for guards/interceptors
export async function ensureFreshToken() {
  const t = getStoredToken();
  if (isTokenValid(t)) return t;
  try {
    const nt = await refreshSession();
    return nt;
  } catch (error) {
    if (isConfirmedInvalidSession(error)) notifyConfirmedInvalidSession();
    return null;
  }
}

export function onTokenChange(cb) {
  function handler(e) {
    if (e && e.key && e.key !== 'token' && e.key !== 'accessToken' && e.key !== 'authToken') return;
    try { cb(getStoredToken()); } catch {}
  }
  const customHandler = () => {
    try { cb(getStoredToken()); } catch {}
  };
  window.addEventListener('storage', handler);
  window.addEventListener('token:changed', customHandler);
  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener('token:changed', customHandler);
  };
}

export function logoutLocal() {
  notifyConfirmedInvalidSession();
}

export async function resendEmailVerification(identifier) {
  const normalizedIdentifier = String(identifier || '').trim();

  if (!normalizedIdentifier) {
    return {
      ok: false,
      code: 'verification_identifier_required',
    };
  }

  const payload = normalizedIdentifier.includes('@')
    ? { email: normalizedIdentifier }
    : { username: normalizedIdentifier };

  try {
    const response = await fetch(`${API_BASE}/api/auth/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    return {
      ok: response.ok,
      code: response.ok
        ? 'verification_resend_processed'
        : 'verification_resend_failed',
      debugVerifyUrl:
        response.ok && typeof data?.debugVerifyUrl === 'string'
          ? data.debugVerifyUrl
          : null,
    };
  } catch (_) {
    return {
      ok: false,
      code: 'verification_resend_failed',
    };
  }
}
